const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const MAX_IMAGES = 20;
const MAX_EXPANDED_BYTES = 32 * 1024 * 1024;
const MAX_XML_BYTES = 2 * 1024 * 1024;
const MAX_RASTER_BYTES = 20 * 1024 * 1024;
const MAX_HTML_BYTES = 30 * 1024 * 1024;
const MAX_FINAL_CHARACTERS = 131072;
const IMAGE_URL_CHARACTERS = 2048;
const MAX_ZIP_ENTRIES = 256;
const MAX_EXPANSION_RATIO = 1000;
const RASTER_PATH = /\.(png|jpe?g|gif|webp)$/i;
const DOCUMENT_TYPES = new Set([
  '', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/octet-stream', 'application/zip',
]);
const IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);

function readBlob(blob, method) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('The document or image could not be read.'));
    reader.onabort = () => reject(new Error('Reading the document or image was cancelled.'));
    reader[method](blob);
  });
}

function hasRasterSignature(bytes, type) {
  const startsWith = (values, offset = 0) => values.every((value, index) => bytes[offset + index] === value);
  switch (type) {
    case 'image/png': return startsWith([137, 80, 78, 71, 13, 10, 26, 10]);
    case 'image/jpeg': return startsWith([255, 216, 255]);
    case 'image/gif': return startsWith([71, 73, 70, 56]) && [55, 57].includes(bytes[4]) && bytes[5] === 97;
    case 'image/webp': return startsWith([82, 73, 70, 70]) && startsWith([87, 69, 66, 80, 86, 80, 56], 8)
      && [32, 76, 88].includes(bytes[15]);
    default: return false;
  }
}

function isHarmlessWarning({ type, message }) {
  // Unmapped styles retain their content. Mammoth 1.12.2 also warns for valid WebP.
  return type === 'warning' && (
    /^Unrecognised (paragraph|run|table) style: /.test(message)
    || /^(Paragraph|Run|Table) style with ID .+ was referenced but not defined in the document$/.test(message)
    || message === 'Image of type image/webp is unlikely to display in web browsers'
  );
}

function validateXml(bytes, name, names, metadata) {
  const text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  // Reject declarations before parsing, so the XML parser never expands a DTD.
  if (/<!\s*(DOCTYPE|ENTITY)\b/i.test(text)) throw new Error('Word XML declarations are not allowed.');
  const xml = new DOMParser().parseFromString(text, 'application/xml');
  if (!xml.documentElement || xml.getElementsByTagName('parsererror').length) {
    throw new Error('Malformed Word XML.');
  }
  const stack = [[xml.documentElement, 0]];
  while (stack.length) {
    const [node, depth] = stack.pop();
    metadata.nodes += 1;
    if (depth > 128 || metadata.nodes > 100000) throw new Error('Word XML is too complex.');
    if (['blip', 'imagedata'].includes(node.localName)) metadata.occurrences += 1;
    if (/\.rels$/i.test(name) && node.localName === 'Relationship') {
      const type = node.getAttribute('Type') || '';
      if (type.endsWith('/image')) metadata.references += 1;
      // Hyperlinks are inert text during conversion; the caller sanitizes them.
      if (!type.endsWith('/hyperlink')) {
        const target = node.getAttribute('Target');
        const base = name.slice(0, name.lastIndexOf('_rels/'));
        const url = target && new URL(target, `https://word-import.invalid/${base}`);
        if (!url || node.getAttribute('TargetMode') === 'External' || url.origin !== 'https://word-import.invalid'
          || url.search || url.hash) throw new Error('External or invalid Word relationships are not allowed.');
        const targetName = decodeURIComponent(url.pathname.slice(1));
        if (!names.has(targetName)) throw new Error('Missing Word relationship target.');
        if (/\/(officeDocument|styles|numbering|footnotes|endnotes|comments)$/.test(type) && !/\.xml$/i.test(targetName)) {
          throw new Error('Word XML relationships must reference XML parts.');
        }
      }
    }
    if (metadata.references > MAX_IMAGES || metadata.occurrences > MAX_IMAGES) {
      throw new Error('Word documents may contain at most 20 images or image references.');
    }
    for (let child = node.lastElementChild; child; child = child.previousElementSibling) stack.push([child, depth + 1]);
  }
}

async function validateArchive(arrayBuffer) {
  const { DecompressionStream, WritableStream } = window;
  if (typeof DecompressionStream !== 'function' || typeof WritableStream !== 'function') {
    throw new Error('This browser does not support safe Word import. Use a current browser.');
  }
  // Native streaming inflation has backpressure; do not fall back to an unbounded JS decoder.
  try { new DecompressionStream('deflate-raw'); } catch {
    throw new Error('This browser does not support safe Word import. Use a current browser.');
  }
  const { ZipReader, Uint8ArrayReader } = await import('@zip.js/zip.js/index-native.js');
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), 10000);
  const reader = new ZipReader(new Uint8ArrayReader(new Uint8Array(arrayBuffer)), {
    strictness: 'strict', checkCrc32: true, checkOverlappingEntry: true,
    useWebWorkers: false, useCompressionStream: true, signal: abort.signal,
  });
  try {
    const entries = [];
    const names = new Set();
    let expanded = 0, rasterBytes = 0, rasterCount = 0;
    // Public metadata only. The generator lets us stop at the entry-count limit.
    for await (const entry of reader.getEntriesGenerator()) {
      const { filename, compressedSize, uncompressedSize } = entry;
      const raster = RASTER_PATH.test(filename);
      if (entries.length >= MAX_ZIP_ENTRIES || names.has(filename) || filename.length > 240
        || /[\\%:]/.test(filename) || [...filename].some(char => char.charCodeAt(0) < 32) || filename.startsWith('/')
        || filename.split('/').some(part => part === '..' || part === '.')) {
        throw new Error('Invalid or excessive Word ZIP entries.');
      }
      if (entry.encrypted || ![0, 8].includes(entry.compressionMethod)
        || !Number.isSafeInteger(compressedSize) || !Number.isSafeInteger(uncompressedSize)
        || compressedSize < 0 || uncompressedSize < 0 || compressedSize > arrayBuffer.byteLength
        || (entry.directory && uncompressedSize !== 0)) throw new Error('Unsupported or malformed Word ZIP entry.');
      if (uncompressedSize > (raster ? MAX_IMAGE_BYTES : MAX_XML_BYTES)) {
        throw new Error(raster ? 'Each Word image must be between 1 byte and 10 MB.' : 'Each Word XML or non-raster part must be at most 2 MiB.');
      }
      expanded += uncompressedSize;
      if (expanded > MAX_EXPANDED_BYTES) throw new Error('Expanded Word archive exceeds 32 MiB.');
      if (raster) {
        rasterBytes += uncompressedSize;
        rasterCount += 1;
        if (rasterBytes > MAX_RASTER_BYTES) throw new Error('Total Word raster images exceed 20 MiB.');
        if (rasterCount > MAX_IMAGES) throw new Error('Word documents may contain at most 20 images.');
      }
      if (uncompressedSize > Math.max(1, compressedSize) * MAX_EXPANSION_RATIO) {
        throw new Error('Suspicious Word ZIP expansion ratio exceeds 1000:1.');
      }
      names.add(filename);
      entries.push(entry);
    }
    if (!names.has('[Content_Types].xml') || !names.has('_rels/.rels')) throw new Error('Not a valid Word document archive.');

    const expansion = { total: 0 };
    const metadata = { nodes: 0, references: 0, occurrences: 0 };
    for (const entry of entries) {
      const isXml = /\.(xml|rels)$/i.test(entry.filename);
      let actualSize = 0;
      const chunks = [];
      await entry.getData(new WritableStream({
        write(chunk) {
          actualSize += chunk.byteLength;
          expansion.total += chunk.byteLength;
          if (actualSize > entry.uncompressedSize || expansion.total > MAX_EXPANDED_BYTES) {
            throw new Error('Word ZIP expansion exceeds its validated bounds.');
          }
          if (isXml) chunks.push(chunk);
        },
      }));
      if (actualSize !== entry.uncompressedSize) throw new Error('Word ZIP size does not match its metadata.');
      if (isXml) {
        const bytes = new Uint8Array(actualSize);
        let offset = 0;
        for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.byteLength; }
        validateXml(bytes, entry.filename, names, metadata);
      }
    }
  } finally {
    clearTimeout(timer);
    abort.abort();
    await reader.close();
  }
}

async function embedImages(markup, images) {
  if (typeof markup !== 'string' || !markup.trim()) throw new Error('The Word document contains no importable body content.');
  // Estimate the final saved HTML before creating any base64 strings.
  if (markup.length + images.reduce((total, image) => total + IMAGE_URL_CHARACTERS - image.marker.length, 0) >= MAX_FINAL_CHARACTERS) {
    throw new Error('Word content exceeds the 131072 character limit after image URL replacement.');
  }
  const template = document.createElement('template');
  template.innerHTML = markup;
  const nodes = [...template.content.querySelectorAll('img')];
  if (nodes.length !== images.length || nodes.some((node, index) => node.getAttribute('src') !== images[index].marker)) {
    throw new Error('Word image conversion was incomplete.');
  }
  const serialized = template.innerHTML;
  const projectedBytes = new Blob([serialized]).size + images.reduce((total, image) =>
    total + `data:${image.type};base64,`.length + 4 * Math.ceil(image.bytes.byteLength / 3) - image.marker.length, 0);
  if (projectedBytes > MAX_HTML_BYTES) throw new Error('Word HTML and image data exceeds 30 MiB.');
  for (const [index, image] of images.entries()) {
    const src = await readBlob(new Blob([image.bytes], { type: image.type }), 'readAsDataURL');
    nodes[index].setAttribute('src', src);
  }
  const html = template.innerHTML;
  if (new Blob([html]).size > MAX_HTML_BYTES) throw new Error('Word HTML and image data exceeds 30 MiB.');
  return html;
}

/**
 * Conversion only: HTML is untrusted and data images are temporary. The caller
 * must sanitize it and replace images through the shared paste/upload engine.
 * ZIP metadata, actual streaming expansion and XML complexity are checked before
 * Mammoth. Native deflate-raw streams are required. Raster pixel/decoder limits
 * and the exact final persisted HTML limit still belong at caller ingestion.
 */
export async function importWordDocument(file) {
  if (!file || typeof file.name !== 'string' || !/\.docx$/i.test(file.name)
    || !DOCUMENT_TYPES.has(String(file.type || '').toLowerCase())) {
    throw new Error('Choose a Word .docx document.');
  }
  if (!Number.isSafeInteger(file.size) || file.size <= 0 || file.size > MAX_DOCUMENT_BYTES) {
    throw new Error('Choose a Word document between 1 byte and 10 MB.');
  }

  try {
    const arrayBuffer = typeof file.arrayBuffer === 'function'
      ? await file.arrayBuffer() : await readBlob(file, 'readAsArrayBuffer');
    if (!arrayBuffer || arrayBuffer.byteLength !== file.size || arrayBuffer.byteLength > MAX_DOCUMENT_BYTES) {
      throw new Error('The document size changed or exceeds 10 MB.');
    }
    await validateArchive(arrayBuffer);
    const module = await import('mammoth/mammoth.browser');
    const mammoth = module.default || module;
    let imageCount = 0;
    let rasterBytes = 0;
    const images = [];
    let imageError;
    const result = await mammoth.convertToHtml({ arrayBuffer }, {
      externalFileAccess: false,
      includeEmbeddedStyleMap: false,
      includeDefaultStyleMap: true,
      ignoreEmptyParagraphs: false,
      convertImage: mammoth.images.imgElement(async image => {
        try {
          if (imageError) throw imageError;
          if (imageCount >= MAX_IMAGES) throw new Error('Word documents may contain at most 20 images.');
          if (!IMAGE_TYPES.has(image.contentType)) {
            throw new Error('Unsupported Word image. Only PNG, JPEG, GIF and WebP are allowed.');
          }
          // Attach rejection handling synchronously to Mammoth's older Bluebird promise.
          const data = await new Promise((resolve, reject) => image.readAsArrayBuffer().then(resolve, reject));
          if (!data || !data.byteLength || data.byteLength > MAX_IMAGE_BYTES) {
            throw new Error('Each Word image must be between 1 byte and 10 MB.');
          }
          rasterBytes += data.byteLength;
          if (rasterBytes > MAX_RASTER_BYTES) throw new Error('Total Word raster images exceed 20 MiB.');
          const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
          if (!hasRasterSignature(bytes, image.contentType)) {
            throw new Error('A Word image does not match its declared PNG, JPEG, GIF or WebP type.');
          }
          const marker = `word-import-image:${imageCount}`;
          images.push({ bytes, type: image.contentType, marker });
          imageCount += 1;
          return { src: marker };
        } catch (error) {
          imageError = error;
          throw error;
        }
      }),
    });
    // Mammoth catches image errors and can resolve with incomplete HTML.
    if (imageError) throw imageError;
    const failure = result.messages.find(message => !isHarmlessWarning(message));
    if (failure) throw new Error(`Conversion would lose content: ${failure.message}`);
    return { html: await embedImages(result.value, images), imageCount };
  } catch (error) {
    throw new Error(`Word document import failed: ${error.message || 'The document could not be converted.'}`);
  }
}
